import { getter, State } from '@progress/kendo-data-query';
import { formatNumber } from '@progress/kendo-intl';
import {
  getSelectedState,
  GridDataStateChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { invoiceColumns } from 'src/assets/static/enter_invoice/invoiceColumns';
import GridWrapper from 'src/components/GridWrapper';
import Modal from 'src/components/Modal';
import useAuth from 'src/hooks/useAuth';
import useCompany from 'src/hooks/useCompany';
import useEnterInvoices from 'src/hooks/useEnterInvoices';
import useWindowSize from 'src/hooks/useWindowSize';
import { IInvoiceDocument } from 'src/types/InvoiceDocument';
import axiosInstance from 'src/utils/axios';
import { modifyRequest } from 'src/utils/requestInterceptor';
import { formatEnterInvoices } from 'src/utils/transformation';
import InvoiceGridToolbar from './InvoiceGridToolBar';
import InvoiceLineItem from './InvoiceLineItem';

const INVOICE_DATE_FIELD = 'statusDate';
const INVOICE_DATA_ITEM_KEY = 'receiptId';
const dateArr = [INVOICE_DATE_FIELD];
const idGetter = getter(INVOICE_DATA_ITEM_KEY);

const initialDocumentDataState: State = {
  skip: 0,
  take: 20,
  sort: [
    {
      field: INVOICE_DATE_FIELD,
      dir: 'asc',
    },
  ],
};

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'single',
};
const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};
const defaultProps = {
  tableTextId: 'Documents',
  DataItemKey: INVOICE_DATA_ITEM_KEY,
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

interface IProps {
  invoiceNumber?: string;
  setSelectedInvoiceRow: React.Dispatch<React.SetStateAction<IInvoiceDocument | null>>;
  poId?: number;
  poNumber?: string;
  showInvoiceSummary?: boolean;
  isDetailsPage: boolean;
}
interface Response {
  items: IInvoiceDocument[];
  totalRecords: number;
}

const DocumentGrid: React.FC<IProps> = ({
  invoiceNumber,
  setSelectedInvoiceRow,
  poId,
  poNumber,
  showInvoiceSummary,
  isDetailsPage,
}) => {
  const { company: selectedCompany } = useCompany();
  const { user } = useAuth();
  const [documentDataState, setDocumentDataState] = useState<State>(initialDocumentDataState);
  const [invoiceListData, setInvoiceListData] = useState<IInvoiceDocument[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lineItem, setLineItem] = useState<IInvoiceDocument>();
  const [lineItemModal, setLineItemModal] = useState<boolean>(false);
  const { invoiceNumber: invoiceNumberValue, showPreview } = useEnterInvoices();

  const { height } = useWindowSize();
  const calculateGridHeight = () => `${height - 198}px`;

  const listCompanyInvoices = useCallback(
    (companyId: string) => {
      const data = { ...modifyRequest(documentDataState, dateArr, invoiceColumns) };
      if (invoiceNumber) {
        const filterByInvoiceNumber = {
          field: 'invoiceNumber',
          value: invoiceNumber,
          operator: 'contains',
        };
        data.filters = [...data.filters, filterByInvoiceNumber];
      } else if (poId) {
        const filterByPoId = {
          field: 'poId',
          value: String(poId),
          operator: 'eq',
        };
        data.filters = [...data.filters, filterByPoId];
      }
      return axiosInstance<Response>('/Invoice/Filter', {
        method: 'POST',
        params: { companyId },
        data,
      });
    },
    [documentDataState, invoiceNumber, poId]
  );
  useEffect(() => {
    if (selectedCompany.companyId || user?.CompanyId) {
      setLoading(true);
      listCompanyInvoices(selectedCompany.companyId || user?.CompanyId)
        ?.then((data) => {
          setInvoiceListData(
            formatEnterInvoices(data.data.items, String(invoiceNumberValue?.value))
          );
          setCount(data.data.totalRecords || 0);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [invoiceNumberValue?.value, listCompanyInvoices, selectedCompany.companyId, user?.CompanyId]);

  const onInvoiceDataStateChange = (e: GridDataStateChangeEvent) => {
    setDocumentDataState(e.dataState);
  };

  const [selectedState, setSelectedState] = useState<{
    [id: string]: boolean | number[];
  }>({});

  const onSelectionInvoiceChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedState,
        dataItemKey: INVOICE_DATA_ITEM_KEY,
      });
      setSelectedState(newSelectedState);
      setInvoiceListData((prev: any) => {
        const selectedPOList = prev.map((item: IInvoiceDocument[]) => ({
          ...item,
          selected: newSelectedState[idGetter(item)],
        }));
        return selectedPOList;
      });
    },
    [selectedState]
  );
  const selectedRow = useMemo(
    () => invoiceListData.filter((item) => item.selected)[0],
    [invoiceListData]
  );

  useEffect(() => {
    setSelectedInvoiceRow(selectedRow);
  }, [selectedRow, setSelectedInvoiceRow]);

  const handleClearFilter = useCallback(() => {
    setDocumentDataState(initialDocumentDataState);
  }, []);

  const handleClearSelection = useCallback(() => {
    setInvoiceListData((prev) => prev.map((item) => ({ ...item, selected: false })));
  }, []);
  const isClearFiltersDisabled = useMemo(
    () => !documentDataState.filter?.filters.length,
    [documentDataState.filter?.filters.length]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <InvoiceGridToolbar
        clearFilter={handleClearFilter}
        clearSelection={handleClearSelection}
        isClearFiltersDisabled={isClearFiltersDisabled}
      />
    ),
    [handleClearFilter, handleClearSelection, isClearFiltersDisabled]
  );

  const toggleLineItemModal = useCallback(() => {
    setLineItemModal((prev) => !prev);
  }, []);

  const cellClick = useCallback(
    (data: any) => {
      setLineItem(data);
      toggleLineItemModal();
    },
    [toggleLineItemModal]
  );

  const handleInvoiceSaveLineItem = () => {
    const selectedInvoiceId = lineItem?.receiptId || '';
    const selectedPo = invoiceListData.map((item) => {
      if (item.receiptId === selectedInvoiceId) {
        return { ...item, selected: true };
      }
      return { ...item, selected: false };
    });
    setInvoiceListData(selectedPo);
    setSelectedInvoiceRow(lineItem || null);

    toggleLineItemModal();
  };
  const toggleInvoiceText = () => {
    const docTotal = selectedRow?.totalCostAmt || 0;
    return docTotal < 0 ? 'Credit Memo' : 'Invoice';
  };
  const invoiceSummary = () => {
    let invoiceTotal = 0;
    let poTotal = invoiceListData[0]?.poTotal;
    invoiceListData.forEach((item) => (invoiceTotal = invoiceTotal + item.totalCostAmt));
    return `Summary-Quantity: PO Total: ${formatNumber(poTotal, 'c')}
     | InvoiceTotal ${formatNumber(invoiceTotal, 'c')}
      Variance ${formatNumber(poTotal - invoiceTotal, 'c')}`;
  };
  return (
    <div className="mr-auto ml-auto mt-2">
      {showInvoiceSummary ? (
        <div className="flex items-center">
          <p className="font-bold text-sm">Showing invoices for PO # "{poNumber}"</p>
          <div className="ml-2 h-8 shadow border border-solid border-gray-300 flex items-center">
            <p className="text-sm font-bold p-2">{invoiceSummary()}</p>
          </div>
        </div>
      ) : invoiceNumber ? (
        <p className="font-bold text-sm">Showing Matching Documents for "{invoiceNumber}"</p>
      ) : poId ? (
        <p className="font-bold text-sm">Showing Documents for PO "{poNumber}"</p>
      ) : null}

      <GridWrapper
        columns={invoiceColumns}
        dataSource={invoiceListData}
        dataState={documentDataState}
        key={`${showPreview}`}
        height={
          !isDetailsPage
            ? calculateGridHeight()
            : height >= 950
            ? `${height - 468}px`
            : height >= 860
            ? `${height - 406}px`
            : `${height - 398}px`
        }
        total={count}
        {...defaultProps}
        onDataStateChange={onInvoiceDataStateChange}
        onSelectionChange={onSelectionInvoiceChange}
        gridToolbarChildren={gridToolbarChildren}
        cellClick={() => ({ cellClick })}
        loading={loading}
      />
      <Modal width="80%" visible={lineItemModal} closeModal={toggleLineItemModal}>
        <InvoiceLineItem
          title={`Showing items for ${toggleInvoiceText()} # ${lineItem?.invoiceNumber || ''}`}
          showModal={toggleLineItemModal}
          invoiceId={lineItem?.receiptId?.toString() || ''}
          saveLineItem={handleInvoiceSaveLineItem}
          isOcr={false}
        />
      </Modal>
    </div>
  );
};

export default DocumentGrid;
